import { useState } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProcessRunListItemType } from '@econtractor/types';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import { deleteProcessRun, launchProcessRun } from 'api/apiTask';
import SpinnerBlock from 'components/others/SpinnerBlock';
import ProcessRunsTableBody from './ProcessRunsTableBody';

interface ProcessRunsTableProps {
    processes: ProcessRunListItemType[];
    showSpinner: boolean;
    onEdit: (process: ProcessRunListItemType) => void;
    onDeleted: () => void;
    onLaunched: () => void;
}

const launchError = (code: string | undefined, t: (key: string) => string): string => {
    if (code === 'ROOT_DUE_REQUIRED') return t('processRuns.errRootDue');
    if (code === 'EMPTY_TYPE') return t('processRuns.errEmptyType');
    if (code === 'NO_ROOT') return t('processRuns.errNoRoot');
    if (code === 'INVALID_TASKS') return t('processRuns.errTasks');
    if (code === 'EXECUTOR_TIME_BUSY') return t('processRuns.errBusy');
    if (code === 'NOT_DRAFT') return t('processRuns.errNotDraft');
    return t('common:messError');
};

const ProcessRunsTable = ({ processes, showSpinner, onEdit, onDeleted, onLaunched }: ProcessRunsTableProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showDlg } = useModal();
    const { showToast } = useToast();
    const [launchingId, setLaunchingId] = useState<number | null>(null);

    const requestLaunch = async (process: ProcessRunListItemType) => {
        if (launchingId) return;
        setLaunchingId(process.ID);
        const res = await launchProcessRun(process.ID);
        setLaunchingId(null);
        if (!res?.stat) {
            showToast(launchError(res?.errCode, t), 'danger');
            return;
        }
        showToast(t('processRuns.messLaunched'), 'success');
        onLaunched();
    };

    const requestDelete = (process: ProcessRunListItemType) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            const res = await deleteProcessRun(process.ID);
            if (!res?.stat) {
                showToast(
                    res?.errCode === 'NOT_DRAFT' ? t('processRuns.errNotDraft') : t('common:messError'),
                    'danger',
                );
                return;
            }
            showToast(t('processRuns.messDeleted'), 'success');
            onDeleted();
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: t('processRuns.confirmDelete', { name: process.name || '' }),
        });
    };

    return (
        <div className="position-relative">
            <SpinnerBlock onlyBlur show={showSpinner} />
            <div className="crm-table-wrap">
                <Table className="ProcessRunsTable table-hover mb-0">
                    <thead>
                        <tr className="bg-light">
                            <td className="head-name">{t('processDir.colName')}</td>
                            <td className="head-created">{t('processRuns.colCreated')}</td>
                            <td className="head-category">{t('processDir.colCategory')}</td>
                            <td className="head-status">{t('processRuns.colStatus')}</td>
                            <td className="head-tasks">{t('processRuns.colTasks')}</td>
                            <td className="head-action" />
                        </tr>
                    </thead>
                    <ProcessRunsTableBody
                        processes={processes}
                        onEdit={onEdit}
                        onLaunch={(process) => void requestLaunch(process)}
                        onDelete={requestDelete}
                        launchingId={launchingId}
                    />
                </Table>
            </div>
        </div>
    );
};

export default ProcessRunsTable;
